import {
  Close,
  DragIndicator,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";

interface Column {
  headerId: string;
  name: string;
  type?: string;
}

interface ColumnOrderDialogProps {
  open: boolean;
  onClose: () => void;
  columns: Column[];
  onSave: (orderedHeaderIds: string[]) => Promise<void>;
}

const ColumnOrderDialog: React.FC<ColumnOrderDialogProps> = ({
  open,
  onClose,
  columns,
  onSave,
}) => {
  const [orderedColumns, setOrderedColumns] = useState<Column[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && columns.length > 0) {
      setOrderedColumns([...columns]);
    }
  }, [open, columns]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedColumns(items);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const orderedHeaderIds = orderedColumns.map((col) => col.headerId);
      await onSave(orderedHeaderIds);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Update Column Order</Typography>
          <IconButton onClick={handleClose} size="small" disabled={saving}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag and drop columns to reorder them. The order from top to bottom
          will be reflected as left to right in the table.
        </Typography>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="columns">
            {(provided) => (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ p: 0 }}
              >
                {orderedColumns.map((column, index) => (
                  <Draggable
                    key={column.headerId}
                    draggableId={column.headerId}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        component={Paper}
                        variant="outlined"
                        sx={{
                          mb: 1,
                          backgroundColor: snapshot.isDragging
                            ? "action.selected"
                            : "background.paper",
                          cursor: "grab",
                          "&:active": {
                            cursor: "grabbing",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <DragIndicator color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={column.name}
                          secondary={
                            column.type === "action" ? "Action Column" : null
                          }
                        />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnOrderDialog;
